<script setup lang="ts">
import Button from 'primevue/button';
import Card from 'primevue/card';
import { computed, ref, watch, type Component } from 'vue';
import { useRouter } from 'vue-router';

import type { AccessRequirementKey } from '@peanuds/app-shell';
import { formatWeaverCurrency } from '@peanuds/shared';
import { useUserAccountsStore } from '@peanuds/shared-state';

import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';

import PromoArea from './components/PromoArea.vue';
import TotalBalanceEmailVerificationDialog from './components/TotalBalanceEmailVerificationDialog.vue';
import TotalBalanceOnboardingContent from './components/TotalBalanceOnboardingContent.vue';
import TotalBalancePhoneVerificationDialog from './components/TotalBalancePhoneVerificationDialog.vue';
import { useTotalBalanceAccessViewModel } from './useTotalBalanceAccessViewModel';
import type { ActionType } from './total-balance.types';

import {
  AddPlusCircleIcon,
  ArrowLeftRightIcon,
  ArrowUpRightLgIcon,
  WalletIcon,
  WalletMultipleIcon,
} from '@peanuds/ui';

const actions: ActionConfig[] = [
  {
    id: 'topup',
    labelKey: 'totalBalance.actions.add_money',
    icon: AddPlusCircleIcon,
    ariaLabelKey: 'totalBalance.actions.add_money',
    route: 'add-money',
  },
  {
    id: 'send',
    labelKey: 'totalBalance.actions.send',
    icon: ArrowUpRightLgIcon,
    ariaLabelKey: 'totalBalance.actions.send',
    route: 'send-money',
  },
  {
    id: 'iban',
    labelKey: 'totalBalance.actions.open_account',
    icon: WalletMultipleIcon,
    ariaLabelKey: 'totalBalance.actions.open_account',
  },
  {
    id: 'exchange',
    labelKey: 'totalBalance.actions.exchange',
    icon: ArrowLeftRightIcon,
    ariaLabelKey: 'totalBalance.actions.exchange',
  },
];

interface ActionConfig {
  id: ActionType;
  labelKey: string;
  icon: Component;
  ariaLabelKey: string;
  route?: string;
}

const { t } = useTranslation();
const router = useRouter();

const userAccountsStore = useUserAccountsStore();
const { ibanAccounts } = storeToRefs(userAccountsStore);
const {
  currentOnboardingItem,
  showOnboardingContent,
  isVerificationLoading,
  isOnboarded,
  isEmailVerificationAvailable,
  handleRequirementCta,
} = useTotalBalanceAccessViewModel();
const isEmailVerificationDialogVisible = ref(false);
const isPhoneVerificationDialogVisible = ref(false);

const totalEurBalance = computed(() => {
  return ibanAccounts.value.reduce((sum, account) => {
    if (account.currency !== 'EUR') {
      return sum;
    }

    const balance = account.availableBalance ?? account.balances?.availableBalance ?? 0;

    return sum + balance;
  }, 0);
});

const isLargeBalance = computed(() => isOnboarded.value && totalEurBalance.value >= 1_000_000);

const isActionDisabled = computed(() => !isOnboarded.value);

const handleOnboardingAction = (key: AccessRequirementKey) => {
  if (key === 'email') {
    if (!isEmailVerificationAvailable.value) {
      return;
    }

    isEmailVerificationDialogVisible.value = true;
    return;
  }

  if (key === 'phone') {
    isPhoneVerificationDialogVisible.value = true;
    return;
  }

  void handleRequirementCta(key);
};

watch(isEmailVerificationAvailable, (isAvailable) => {
  if (!isAvailable) {
    isEmailVerificationDialogVisible.value = false;
  }
});

const handleActionClick = (action: ActionType) => {
  if (isActionDisabled.value) {
    return;
  }

  switch (action) {
    case 'topup':
    case 'send': {
      const actionConfig = actions.find((entry) => entry.id === action);
      if (actionConfig?.route) {
        router.push({ name: actionConfig.route });
      }
      break;
    }
    case 'iban':
      router.push({ name: 'open-account' });
      break;
    case 'exchange':
      router.push({ name: 'accounts' });
      break;
  }
};
</script>

<template>
  <Card class="total-balance dots-pattern-2" data-testid="dashboard-total-balance-card">
    <template #title>
      <div class="total-balance__header">
        <div class="total-balance__label-row">
          <WalletIcon class="total-balance__icon" />
          <span class="total-balance__label">{{ t('totalBalance.title') }}</span>
        </div>
        <span
          :class="[
            'total-balance__value',
            {
              'total-balance__value--positive': totalEurBalance > 0 && isOnboarded,
              'total-balance__value--large': isLargeBalance,
            },
          ]"
          data-testid="dashboard-total-balance-value"
        >
          {{
            totalEurBalance > 0 && isOnboarded ? formatWeaverCurrency(totalEurBalance) : '€ 0,00'
          }}
        </span>
      </div>
    </template>

    <template #subtitle>
      <div class="total-balance__actions">
        <button
          v-for="action in actions"
          :key="action.id"
          type="button"
          class="total-balance__action"
          :class="{ 'total-balance__action--disabled': isActionDisabled }"
          :disabled="isActionDisabled"
          :aria-label="t(action.ariaLabelKey)"
          @click="handleActionClick(action.id)"
        >
          <Button
            rounded
            severity="primary"
            :disabled="isActionDisabled"
            class="total-balance__action-icon"
            :aria-hidden="true"
            tabindex="-1"
          >
            <template #icon>
              <component :is="action.icon" />
            </template>
          </Button>

          <div class="total-balance__action-label">
            <span class="label">
              {{ t(action.labelKey) }}
            </span>
          </div>
        </button>
      </div>
    </template>

    <template #content>
      <PromoArea v-if="isOnboarded" />
      <div
        v-else-if="showOnboardingContent && currentOnboardingItem"
        class="total-balance__content"
      >
        <TotalBalanceOnboardingContent
          :current-item="currentOnboardingItem"
          :loading="isVerificationLoading"
          @action="handleOnboardingAction"
        />
      </div>
    </template>
  </Card>
  <TotalBalanceEmailVerificationDialog v-model:visible="isEmailVerificationDialogVisible" />
  <TotalBalancePhoneVerificationDialog v-model:visible="isPhoneVerificationDialogVisible" />
</template>

<style lang="scss" scoped>
.total-balance {
  // PrimeVue Card overrides - style the Card component itself
  border: 1px solid var(--p-surface-200);
  border-radius: var(--p-border-radius-xxl);
  background: var(--p-surface-100);

  :deep(.p-card-body) {
    position: relative;
    z-index: 1;
    gap: $space-lg;
    padding: $card-padding-lg;
  }

  :deep(.p-card-caption) {
    gap: $space-lg;
  }

  &__icon {
    width: $static-icon-sm;
    height: $static-icon-sm;
  }

  // Content area (onboarding states)
  &__content {
    border-radius: var(--p-border-radius-xl);
    background-color: var(--p-surface-900);
    padding: $space-sm;
  }

  &__label-row {
    display: flex;
    gap: $gap-xs;
    align-items: center;
    margin-bottom: $space-lg;
  }

  &__label {
    font-size: $text-md;
    font-weight: $font-medium;
    color: var(--p-surface-500);
    line-height: $leading-tight;
  }

  &__value {
    font-size: $balance-value;
    font-weight: $font-medium;
    color: var(--p-surface-500);
    line-height: $leading-none;
    letter-spacing: $tracking-tight;

    &--positive {
      color: var(--p-surface-700);
    }

    &--large {
      font-size: f(18, 32);
    }
  }

  // Actions grid
  &__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: 1fr;
    gap: $gap-xs;
  }

  &__action {
    display: flex;
    flex-direction: column;
    gap: $gap-xs;
    align-items: center;
    background-color: var(--p-primary-contrast-color);
    border: none;
    border-radius: $radius-xl;
    padding: f(8, 18) f(16, 20);
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
      background-color: rgb(255 255 255 / 0.6);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__action-icon {
    padding: 0;
    width: $button-icon;
    height: $button-icon;
    pointer-events: none;

    :deep(.p-button-icon) {
      font-size: $static-text-md;
    }
  }

  &__action-label {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    font-size: $text-md;
    font-weight: $font-medium;
    line-height: $leading-none;
    letter-spacing: $tracking-body;
    color: var(--p-surface-600);
    font-weight: $font-medium;

    .label {
      text-align: center;

      @media screen and (max-width: $screen_normal) {
        // Multi-line ellipsis
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        text-wrap: wrap;
      }
    }

    .total-balance__action:disabled & {
      color: var(--p-surface-500);
    }
  }
}
</style>
